import api, { withLoading } from '@/api/api';

/**
 * @typedef {object} WorkflowNivelHierarquicoTipo
 * @property {number} id - Identificador único do nível hierárquico
 * @property {string} nome - Nome do nível hierárquico
 * @property {string} slug - Slug do nível hierárquico
 */

/**
 * @typedef {object} WorkflowStatusTipo
 * @property {number} id - Identificador único do status
 * @property {string} slug - Slug do status (aguardando, em_analise, ajuste, aprovado, reprovado)
 * @property {string} nome - Nome do status
 * @property {boolean} obrigatorio - Se o status é obrigatório na etapa
 * @property {"demandante"|"revisores"} [responsavel] - Responsável pelas ações deste status (STUB; backend não expõe ainda)
 */

/**
 * @typedef {object} WorkflowStatus
 * @property {number} id - Identificador único do status da etapa
 * @property {number} workflow_id - ID da etapa (workflow)
 * @property {number} status_id - ID do tipo de status
 * @property {WorkflowStatusTipo} tipo - Tipo de status
 * @property {boolean} definir_revisores - Se os revisores da etapa são escolhidos manualmente
 * @property {number|null} sla - SLA em dias úteis para este status
 * @property {Encaminhamento[]|null} encaminhamentos - Transições configuradas (array de encaminhamentos)
 * @property {boolean} [inicial] - **PROPOSTO (pendente de backend):** marca o status de **entrada** do
 * fluxo. Ver a proposta de alteração de API abaixo (bug 4523). Enquanto não existir, o front resolve a
 * entrada por heurística e bloqueia o envio quando não consegue (`encaminhamentoValido`).
 */

/**
 * @typedef {object} Workflow
 * @property {number} id - Identificador único da etapa
 * @property {number} empresa_id - ID da empresa
 * @property {string} nome - Nome da etapa
 * @property {number|null} nivel_hierarquico_tipo_id - Nível hierárquico exigido para revisores
 * @property {WorkflowNivelHierarquicoTipo|null} nivel_hierarquico_tipo - Detalhe do nível hierárquico
 * @property {{ min: number, max: number|null }|null} aprovadores - Configuração de quórum
 * @property {WorkflowStatus[]} status - Status/encaminhamentos da etapa
 */

/**
 * @typedef {object} Encaminhamento
 * @property {string} [nome] - Rótulo livre da transição (usado nos encaminhamentos do status)
 * @property {number} [workflow_id] - Etapa de destino (ausente quando `final` é usado)
 * @property {number} [status_id] - Status de destino (2 em análise, 3 ajuste, 4 aprovado, 5 reprovado)
 * @property {"aprovado"|"reprovado"} [final] - Destino terminal do workflow
 */

/**
 * @typedef {object} WorkflowPayload
 * @property {string} nome - Nome da etapa
 * @property {number|null} nivel_hierarquico_tipo_id - Nível hierárquico exigido para revisores
 * @property {{ min: number, max: number|null }} [aprovadores] - Configuração de quórum
 * @property {number} [ordem] - Ordem da etapa (NULL/<= 0 = vai para o fim). @deprecated O front de Configurações > Workflow não envia mais este campo.
 */

/**
 * @typedef {object} WorkflowStatusPayload
 * @property {number} status_id - ID do tipo de status (2 em análise, 3 ajuste, 4 aprovado, 5 reprovado).
 * Obrigatório no create (WorkflowStatusController@create).
 * @property {boolean} definir_revisores - Se os revisores da etapa são escolhidos manualmente
 * @property {number|null} sla - SLA em dias úteis para este status (min 1, max 365)
 * @property {Encaminhamento[]|null} encaminhamentos - Transições configuradas (array) ou null
 */

/**
 * @typedef {object} WorkflowStatusUpdatePayload
 * @property {boolean} definir_revisores - Se os revisores da etapa são escolhidos manualmente
 * @property {number|null} sla - SLA em dias úteis para este status (min 1, max 365)
 * @property {Encaminhamento[]|null} encaminhamentos - Transições configuradas (array) ou null
 */

/**
 * @typedef {object} AprovadoresPayload
 * @property {number} min - Quantidade mínima de aprovadores
 * @property {number|null} max - Quantidade máxima de aprovadores (null = sem limite)
 */

/**
 * @typedef {object} ProcessarWorkflowData
 * @property {Encaminhamento} encaminhamento - Encaminhamento de destino
 * @property {string} comentario - Comentário/justificativa (obrigatório)
 * @property {number[]} [responsaveis] - IDs de responsáveis (obrigatório se o status de destino tiver definir_revisores = true)
 */

/**
 * @typedef {object} GerenciarRevisoresData
 * @property {number[]} revisores - IDs de responsáveis
 * @property {number} [workflow_id] - Etapa alvo (usa a etapa atual do BP se omitido)
 * @property {boolean} [remover] - true remove vínculos; false/omitido adiciona
 */

const resource = {
  workflows: (empresaId) => `v1/ativacaoped/${empresaId}/workflows`,
  workflowStatus: (empresaId, workflowId) =>
    `v1/ativacaoped/${empresaId}/workflows/${workflowId}/status`,
  businessPlansWorkflow: (empresaId) =>
    `v1/ativacaoped/${empresaId}/business-plans-workflow`,
  businessPlanWorkflow: (empresaId, businessPlanId) =>
    `v1/ativacaoped/${empresaId}/business-plans-workflow/${businessPlanId}`
};

const LOADING_KEY = 'ativacaoped-workflow';

/**
 * Stub dos tipos de status possíveis para uma etapa de parametrização.
 * Fixo no front até o backend expor o endpoint correspondente.
 * O campo `responsavel` indica quem executa as ações do status:
 * "demandante" (quem preenche o BP) ou "revisores" (aprovadores/analistas).
 * @type {WorkflowStatusTipo[]}
 */
const WORKFLOW_STATUS_TIPOS_STUB = [
  { id: 1, slug: "aguardando", nome: "Aguardando", obrigatorio: false, responsavel: "revisores" },
  { id: 2, slug: "em_analise", nome: "Em Análise", obrigatorio: true, responsavel: "revisores" },
  { id: 3, slug: "ajuste", nome: "Ajuste", obrigatorio: false, responsavel: "demandante" },
  { id: 4, slug: "aprovado", nome: "Aprovado", obrigatorio: true, responsavel: "revisores" },
  { id: 5, slug: "reprovado", nome: "Reprovado", obrigatorio: false, responsavel: "revisores" }
];

/**
 * ⚠️ PROPOSTA DE ALTERAÇÃO NA API — “status de entrada do fluxo” (**pendente de backend**)
 *
 * **Motivo (bug 4523):** hoje o front descobre a etapa/status de entrada por **heurística**
 * (`inicioWorkflow` + `preverCaminhoPrincipal` em `config/workflow.helpers.js`). A heurística depende da
 * forma do grafo e, quando falha, o envio do BP sai sem `workflow_id`/`status_id` e o backend responde
 * 422 (“O campo encaminhamento workflow id é obrigatório”).
 *
 * **Contrato sugerido:**
 * 1. `atv_cad_workflow_status.inicial` (boolean, `default false`), **único por empresa**
 *    (índice único parcial em `empresa_id` onde `inicial = true`);
 * 2. expor `inicial` no `WorkflowStatusResource` — já chega aninhado em
 *    `GET /v1/ativacaoped/:empresaId/workflows` (`data[].status[].inicial`);
 * 3. aceitar `inicial` no create/update do status, desmarcando os demais da empresa na mesma transação;
 * 4. **(opcional, mais simples para o front)** `GET /v1/ativacaoped/:empresaId/workflows/inicial`
 *    devolvendo `{ workflow_id, status_id }` — dispensa o front montar o grafo.
 *
 * **Migração de dados:** marcar como `inicial`, por empresa, o status que a heurística atual resolve,
 * preservando o comportamento no rollout.
 *
 * **Como o front consumiria:** `inicioWorkflow()` prioriza o status com `inicial === true` e mantém a
 * heurística como **fallback** — nenhuma outra mudança de contrato é necessária.
 *
 * **Enquanto o backend não expõe:** o front (a) resolve por heurística e (b) **bloqueia** o envio quando
 * não obtém um par `workflow_id`/`status_id` válido (`encaminhamentoValido`, bug 4523) — em vez de deixar
 * o backend devolver o 422 cru.
 *
 * @see docs_agent/tarefas/4358-workflow-agnostico/bug-4523-workflow-id-obrigatorio.md (§5.1)
 */
export default {
  /**
   * Retorna os tipos de status possíveis para uma etapa de parametrização.
   * STUB: endpoint ainda não implementado no backend. Quando implementado,
   * adicionar o parâmetro empresaId e substituir a linha interna por:
   * `api.get(\`...\`).then(res => res.data.data)`
   * TODO: quando o backend expuser `responsavel` no status/tipo,
   * remover o campo do stub e usar o value vindo da API.
   * @returns {Promise<WorkflowStatusTipo[]>}
   */
  getWorkflowStatusTipos() {
    return withLoading(LOADING_KEY, () =>
      Promise.resolve(WORKFLOW_STATUS_TIPOS_STUB.map(t => ({ ...t })))
    );
  },

  /**
   * Lista as etapas do workflow, ordenadas por ordem (NULL → fim).
   * GET /v1/ativacaoped/:empresaId/workflows
   * @param {number} empresaId
   * @param {object} [params={}] - optional query parameters
   * @returns {Promise<Workflow[]>}
   */
  getWorkflows(empresaId, params = {}) {
    const query = Object.keys(params).length
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return withLoading(LOADING_KEY, () =>
      api.get(`${resource.workflows(empresaId)}${query}`).then(res => res.data.data)
    );
  },

   /**
    * Cria uma nova etapa de workflow.
    * POST /v1/ativacaoped/:empresaId/workflows
    * @param {number} empresaId
    * @param {WorkflowPayload} data - payload da criação da etapa
    * @returns {Promise<Workflow>}
    */
  createWorkflow(empresaId, data) {
    return withLoading(LOADING_KEY, () =>
      api.post(resource.workflows(empresaId), data).then(res => res.data.data)
    );
  },

  /**
   * Cria o workflow padrão (Análise + terminais) se a empresa não tiver etapas.
   * POST /v1/ativacaoped/:empresaId/workflows/padrao
   * @param {number} empresaId
   * @returns {Promise<Workflow[]>}
   */
  createWorkflowPadrao(empresaId) {
    return withLoading(LOADING_KEY, () =>
      api.post(`${resource.workflows(empresaId)}/padrao`).then(res => res.data.data)
    );
  },

   /**
    * Atualiza uma etapa de workflow.
    * PUT /v1/ativacaoped/:empresaId/workflows/:workflowId
    * @param {number} empresaId
    * @param {number} workflowId
    * @param {WorkflowPayload} data - payload da atualização da etapa
    * @returns {Promise<Workflow>}
    */
  updateWorkflow(empresaId, workflowId, data) {
    return withLoading(LOADING_KEY, () =>
      api.put(`${resource.workflows(empresaId)}/${workflowId}`, data).then(res => res.data.data)
    );
  },

  /**
   * Exclui (soft delete) uma etapa de workflow. Bloqueado se houver BPs vinculados.
   * DELETE /v1/ativacaoped/:empresaId/workflows/:workflowId
   * @param {number} empresaId
   * @param {number} workflowId
   * @returns {Promise<void>}
   */
  deleteWorkflow(empresaId, workflowId) {
    return withLoading(LOADING_KEY, () =>
      api.delete(`${resource.workflows(empresaId)}/${workflowId}`).then(res => res.data)
    );
  },

   /**
    * Adiciona um status/encaminhamento à etapa.
    * POST /v1/ativacaoped/:empresaId/workflows/:workflowId/status
    * @param {number} empresaId
    * @param {number} workflowId
    * @param {WorkflowStatusPayload} data - payload do status da etapa
    * @returns {Promise<WorkflowStatus>}
    */
  addWorkflowStatus(empresaId, workflowId, data) {
    return withLoading(LOADING_KEY, () =>
      api.post(resource.workflowStatus(empresaId, workflowId), data).then(res => res.data.data)
    );
  },

   /**
    * Atualiza um status/encaminhamento da etapa.
    * PUT /v1/ativacaoped/:empresaId/workflows/:workflowId/status/:workflowStatusId
    * @param {number} empresaId
    * @param {number} workflowId
    * @param {number} workflowStatusId
    * @param {WorkflowStatusUpdatePayload} data - payload de atualização do status da etapa
    * @returns {Promise<WorkflowStatus>}
    */
  updateWorkflowStatus(empresaId, workflowId, workflowStatusId, data) {
    return withLoading(LOADING_KEY, () =>
      api
        .put(`${resource.workflowStatus(empresaId, workflowId)}/${workflowStatusId}`, data)
        .then(res => res.data.data)
    );
  },

  /**
   * Remove um status da etapa (bloqueado para em_analise e aprovado).
   * DELETE /v1/ativacaoped/:empresaId/workflows/:workflowId/status/:workflowStatusId
   * @param {number} empresaId
   * @param {number} workflowId
   * @param {number} workflowStatusId
   * @returns {Promise<void>}
   */
  removeWorkflowStatus(empresaId, workflowId, workflowStatusId) {
    return withLoading(LOADING_KEY, () =>
      api
        .delete(`${resource.workflowStatus(empresaId, workflowId)}/${workflowStatusId}`)
        .then(res => res.data)
    );
  },

   /**
    * Transição unificada do workflow: o backend decide se é voto, avanço, ajuste ou reprovação.
    * POST /v1/ativacaoped/:empresaId/business-plans-workflow/:businessPlanId
    * @param {number} empresaId
    * @param {number} businessPlanId
    * @param {ProcessarWorkflowData} data - payload da transição do workflow
    * @returns {Promise<object>}
    */
  processarWorkflow(empresaId, businessPlanId, data) {
    return withLoading(LOADING_KEY, () =>
      api
        .post(resource.businessPlanWorkflow(empresaId, businessPlanId), data)
        .then(res => res.data.data)
    );
  },

   /**
    * Gestão de revisores: vincula ou remove revisores de uma etapa do BP.
    * POST /v1/ativacaoped/:empresaId/business-plans-workflow/:businessPlanId/revisores
    * @param {number} empresaId
    * @param {number} businessPlanId
    * @param {GerenciarRevisoresData} data - payload da gestão de revisores
    * @returns {Promise<object>}
    */
  gerenciarRevisores(empresaId, businessPlanId, data) {
    return withLoading(LOADING_KEY, () =>
      api
        .post(`${resource.businessPlanWorkflow(empresaId, businessPlanId)}/revisores`, data)
        .then(res => res.data.data)
    );
  },

  /**
   * Acompanhamento paginado dos BPs em workflow (endpoint PAGINADO).
   * GET /v1/ativacaoped/:empresaId/business-plans-workflow
   *
   * Retorna o CORPO da API (não apenas a lista): `{ data, meta, links }` — o
   * `meta` carrega a paginação consumida pela tela de Acompanhamento.
   * @param {number} empresaId
   * @param {object|string} [params={}] - objeto de query params (page, per_page,
   * filters[responsaveis], filters[nome], filters[workflow_id]) OU query string
   * já montada (ex.: "page=1&per_page=15")
   * @returns {Promise<{data: object[], meta: object, links?: object}>} corpo paginado
   */
  getBusinessPlansWorkflow(empresaId, params = {}) {
    const query =
      typeof params === "string"
        ? params
        : new URLSearchParams(params).toString();
    const suffix = query ? `?${query}` : "";
    return withLoading(LOADING_KEY, () =>
      api
        .get(`${resource.businessPlansWorkflow(empresaId)}${suffix}`)
        .then(res => res.data)
    );
  }
};